<template>
    <button
        :type="type"
        :class="[
            'amo-btn',
            fontWeight,
            {
                'amo-btn--primary': !(
                    secondary ||
                    black ||
                    danger ||
                    dangerSoft ||
                    tertiary
                ),
                'amo-btn--secondary': secondary,
                'amo-btn--danger': danger,
                'amo-btn--tertiary': tertiary,
                'amo-btn--tertiary': black,
                'amo-btn--sm': small,
                'amo-btn--danger-soft': dangerSoft,
            },
            { 'cursor-not-allowed opacity-50': disabled || loading },
            {
                'bg-transparent': bgTransparent,
                'border-0': border0,
                'p-0': p0,
            },
            actionClass,
            customClass,
        ]"
        :style="[
            { width: width || 'auto' },
            textColor ? { color: textColor } : {},
            backgroundColor ? { backgroundColor: backgroundColor } : {},
            fontWeight ? { fontWeight: fontWeight } : {},
        ]"
        :disabled="disabled || loading"
        :data-testid="dataTestid || undefined"
        @click="handleClick"
    >
        <i v-if="loading" class="spinner2"></i>
        <!-- Loading Spinner -->
        <i v-if="icon && !loading" :class="icon"></i>
        <!-- Lock on the left when there is NO icon -->
        <span
            v-if="
                shouldShowUpgradeLock &&
                !loading &&
                !icon &&
                !hideLockIcon &&
                !autoHideLockForActions
            "
        >
            <i class="fas fa-lock"></i>
        </span>
        <slot name="svg" v-if="!loading" />
        <slot v-if="!loading" />
        <!-- Lock on the right when there IS an icon -->
        <span
            v-if="
                shouldShowUpgradeLock &&
                !loading &&
                icon &&
                !hideLockIcon &&
                !autoHideLockForActions
            "
            class="ml-1"
        >
            <i class="fas fa-lock"></i>
        </span>
        <span v-if="loading">{{ loadingText }}</span>
    </button>
</template>

<script>
import { showUpgradeModal } from "@/services/upgradeModalService";
export default {
    name: "BaseButton",
    props: {
        type: { type: String, default: "button" },
        disabled: { type: Boolean, default: false },
        customClass: { type: [String, Array, Object], default: "" },
        width: { type: String, default: "" },
        action: { type: String, default: "confirm" }, // 'confirm', 'cancel', 'remove'
        icon: { type: String, default: "" }, // Icon class
        loading: { type: Boolean, default: false }, // New loading prop
        secondary: { type: Boolean, default: false },
        dangerSoft: { type: Boolean, default: false },
        danger: { type: Boolean, default: false },
        black: { type: Boolean, default: false },
        tertiary: { type: Boolean, default: false },
        small: { type: Boolean, default: false },
        textColor: { type: String, default: "" },
        backgroundColor: { type: String, default: "" },
        fontWeight: {
            type: String,
            default: "",
        },

        // Utility class toggles
        bgTransparent: { type: Boolean, default: false },
        border0: { type: Boolean, default: false },
        p0: { type: Boolean, default: false },
        // When provided, clicking the button will navigate to this URL (used for downloads/links)
        downloadUrl: { type: String, default: "" },
        loadingText: {
            type: String,
            default: "Loading",
        },
        // Plan requirement (use as requires-plan="pro" | "premium").
        // Also accept common typo via equires-plan
        requiresPlan: { type: String, default: "pro" },
        equiresPlan: { type: String, default: "" },
        // Hide the lock icon while keeping restriction behavior (useful for edit/remove/delete/rename)
        hideLockIcon: { type: Boolean, default: false },
        // When true, bypass any plan restriction check and lock icon
        ignorePlanRestriction: { type: Boolean, default: false },
        upgradeContextKey: { type: String, default: "" },
        upgradePattern: { type: String, default: "locked-action" },
        // Test ID for e2e testing
        dataTestid: { type: String, default: "" },
    },
    computed: {
        actionClass() {
            switch (this.action) {
                case "cancel":
                    return "cancel-button";
                case "remove":
                    return "remove-button";
                default:
                    return "amo-btn";
            }
        },
        currentPlan() {
            // Read from Inertia page props; default to 'free' if missing
            return (this.$page?.props?.auth?.user?.plan || "free")
                .toString()
                .toLowerCase();
        },
        isManagerAccount() {
            return this.$page?.props?.auth?.isManager === true;
        },
        normalizedRequiresPlan() {
            // Manager accounts do not have subscriptions of their own. Customer
            // plan restrictions resume while a manager is impersonating a user.
            if (this.ignorePlanRestriction || this.isManagerAccount) return "";
            const raw = (this.requiresPlan || this.equiresPlan || "")
                .toString()
                .toLowerCase()
                .trim();
            if (!raw) return "";
            if (raw === "premium") return "premium"; // handle typo
            // Only allow known values
            return ["pro", "premium"].includes(raw) ? raw : "";
        },
        targetPlanForUpgrade() {
            // If user lacks required plan, this is the plan to show in the modal
            const req = this.normalizedRequiresPlan;
            if (!req) return "";
            const rank = { free: 0, pro: 1, premium: 2 };
            const userRank = rank[this.currentPlan] ?? 0;
            const reqRank = rank[req] ?? 0;
            return userRank >= reqRank ? "" : req;
        },
        shouldShowUpgradeLock() {
            // Show lock icon only when a higher plan is required and user does not meet it
            return !!this.targetPlanForUpgrade;
        },
        autoHideLockForActions() {
            const act = (this.action || "").toString().toLowerCase();
            return ["edit", "remove", "delete", "rename"].includes(act);
        },
    },
    methods: {
        handleClick(event) {
            if (this.disabled || this.loading) return;

            // If a higher plan is required and user does not meet it, show upgrade modal
            if (this.normalizedRequiresPlan) {
                const rank = { free: 0, pro: 1, premium: 2 };
                const userRank = rank[this.currentPlan] ?? 0;
                const requiredRank = rank[this.normalizedRequiresPlan] ?? 0;
                if (userRank < requiredRank) {
                    if (this.upgradeContextKey) {
                        showUpgradeModal({
                            requiredPlan: this.targetPlanForUpgrade,
                            contextKey: this.upgradeContextKey,
                            pattern: this.upgradePattern || "locked-action",
                        });
                    } else {
                        showUpgradeModal(this.targetPlanForUpgrade);
                    }
                    return;
                }
            }
            this.$emit("buttonClick", event);
        },
    },
};
</script>
